import { useEffect, useState } from "react";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Phone, Mail, Moon, Sun } from "lucide-react";

export default function ProfileModal({ open, onClose }) {
  const { user, refreshUser } = useAuth();
  const { cc, toggleCc } = useTheme();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [defaultSilent, setDefaultSilent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setPhone(user?.phone || "");
      api.get("/profile").then((r) => setDefaultSilent(!!r.data.default_silent)).catch(() => {});
    }
  }, [open, user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile", { name, phone, default_silent: defaultSilent });
      await refreshUser();
      toast.success("Contact details saved");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} testid="profile-modal" subtitle="Where reminders reach you" title="My Contact">
      <div className="space-y-4">
        <div>
          <span className="label-mono text-[var(--text-2)] flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Account email</span>
          <input data-testid="profile-email" value={user?.email || ""} readOnly className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-[#f1f1ee] text-[var(--text-2)] outline-none" />
        </div>
        <div>
          <span className="label-mono text-[var(--text-2)]">Full name</span>
          <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
        </div>
        <div>
          <span className="label-mono text-[var(--text-2)] flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Mobile number</span>
          <input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 305 555 0100" className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
        </div>
        <label className="flex items-center justify-between gap-3 border border-[var(--line)] bg-white p-3 cursor-pointer">
          <span className="text-sm">Default new reminders to <strong>Silent</strong> — shown in-app only, no messages sent</span>
          <input type="checkbox" data-testid="profile-default-silent" checked={defaultSilent} onChange={(e) => setDefaultSilent(e.target.checked)} className="w-5 h-5 accent-[var(--accent)] shrink-0" />
        </label>
        <label className="flex items-center justify-between gap-3 border border-[var(--line)] bg-white p-3">
          <span className="text-sm flex items-center gap-2">{cc ? <Moon className="w-4 h-4 text-[var(--accent)]" /> : <Sun className="w-4 h-4 text-[var(--accent)]" />} <span><strong>Command Center</strong> dark mode — applies across your Briefing & dashboards</span></span>
          <button data-testid="profile-theme-toggle" onClick={toggleCc} role="switch" aria-checked={cc} className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${cc ? "bg-[var(--accent)]" : "bg-[var(--line)]"}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${cc ? "translate-x-5" : ""}`} />
          </button>
        </label>
        <p className="text-xs text-[var(--text-2)] border border-[var(--line)] bg-[#f9f9f7] p-3">
          Your personal reminders auto-send here: <strong>voice calls & texts</strong> to this number, <strong>emails</strong> to your account address — no need to retype recipients on every task.
        </p>
        <div className="flex gap-3 pt-1">
          <button data-testid="profile-cancel" onClick={onClose} className="flex-1 h-11 border border-[var(--line)] hover:bg-white transition-colors">Cancel</button>
          <button data-testid="profile-save" onClick={save} disabled={busy} className="flex-1 h-11 bg-[var(--accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50">
            {busy ? "Saving…" : "Save contact"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
